/**
 * SubjectFormModal - Модальное окно создания/редактирования предмета
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Switch,
} from '@mantine/core';
import { IconCheck, IconBook } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

const SubjectFormModal = ({ opened, onClose, onSuccess, subject }) => {
  const isEdit = !!subject;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  // Заполнение формы при редактировании
  useEffect(() => {
    if (subject) {
      setFormData({
        name: subject.name || '',
        description: subject.description || '',
        is_active: subject.is_active ?? true,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
    }
  }, [subject, opened]);

  const handleSubmit = async () => {
    // Валидация
    if (!formData.name.trim()) {
      notifications.show({
        title: 'Ошибка',
        message: 'Введите название предмета',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const url = isEdit ? `/api/subjects/${subject.id}` : '/api/subjects';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Ошибка сохранения');
      }

      notifications.show({
        title: 'Успешно',
        message: isEdit ? 'Предмет обновлён' : 'Предмет создан',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      onSuccess();
    } catch (err) {
      console.error('Failed to save subject:', err);
      notifications.show({
        title: 'Ошибка',
        message: err.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактирование предмета' : 'Новый предмет'}
      centered
      radius="lg"
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label="Название"
          placeholder="Например: Робототехника"
          required
          leftSection={<IconBook size={16} />}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        <Textarea
          label="Описание"
          placeholder="Описание предмета (опционально)"
          minRows={3}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        />

        {isEdit && (
          <Switch
            label="Активен"
            description="Неактивные предметы не отображаются при выборе"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.currentTarget.checked })}
          />
        )}

        <Group justify="flex-end" gap="sm" mt="md">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            leftSection={<IconCheck size={16} />}
          >
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SubjectFormModal;
